import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/modules/auth/stores/authStore';
import { wsService } from '@/services/websocket';
import { api } from '@/services/api';
import { AdBanner, AD_SLOTS } from '@/modules/ads';
import { getLoginUrl } from '@/modules/auth/services/authService';

interface UserStats {
  level: number;
  tier: string;
  tierEmoji: string;
  title: string;
  debaterCount: number;
  juryCount: number;
  wins: number;
  losses: number;
  draws: number;
  winRate: number;
  juryPriority: number;
}

export default function LobbyPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isGuest, isAdmin, checkAdminStatus } = useAuthStore();
  
  // 관리자 여부 재확인 (페이지 진입 시)
  useEffect(() => {
    if (isAuthenticated) {
      checkAdminStatus();
    }
  }, [checkAdminStatus, isAuthenticated]);

  const [roomStatus, setRoomStatus] = useState<{
    status: string;
    hasActiveGame: boolean;
    canSpectate: boolean;
  } | null>(null);

  const [userStats, setUserStats] = useState<UserStats>({
    level: 1,
    tier: '브론즈',
    tierEmoji: '🥉',
    title: '신입 변론가',
    debaterCount: 0,
    juryCount: 0,
    wins: 0,
    losses: 0,
    draws: 0,
    winRate: 0,
    juryPriority: 0,
  });
  const [statsLoading, setStatsLoading] = useState(true);

  // 방 상태 조회 (5초마다 폴링)
  useEffect(() => {
    async function checkRoomStatus() {
      try {
        await wsService.connect();
        wsService.send({ action: 'GET_ROOM_STATUS' });
      } catch (err) {
        console.error('Failed to check room status:', err);
      }
    }

    // 초기 조회
    checkRoomStatus();
    
    // 5초마다 폴링
    const pollInterval = setInterval(() => {
      if (wsService.isConnected) {
        wsService.send({ action: 'GET_ROOM_STATUS' });
      } else {
        checkRoomStatus();
      }
    }, 5000);

    const unsubRoomStatus = wsService.on('ROOM_STATUS', (data) => {
      console.log('Room status updated:', data);
      setRoomStatus({
        status: data.status,
        hasActiveGame: data.hasActiveGame,
        canSpectate: data.canSpectate,
      });
    });

    return () => {
      clearInterval(pollInterval);
      unsubRoomStatus();
    };
  }, []);

  // 사용자 통계 조회 (REST API 사용) - 로그인 사용자만
  useEffect(() => {
    if (!user?.userId || isGuest) {
      setStatsLoading(false);
      return;
    }

    const fetchStats = async () => {
      try {
        setStatsLoading(true);
        const data = await api.get<UserStats>('/users/me/stats');
        setUserStats(data);
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      } finally {
        setStatsLoading(false);
      }
    };

    fetchStats();
  }, [user?.userId, isGuest]);

  // 계산된 통계
  const totalDebates = userStats.debaterCount;
  const winRateDisplay = totalDebates > 0 
    ? (userStats.winRate * 100).toFixed(0) 
    : '0';

  const handleStartDebate = () => {
    // 게스트는 토론 참여 불가
    if (isGuest || !isAuthenticated) {
      alert('토론에 참여하려면 로그인이 필요합니다.');
      window.location.href = getLoginUrl();
      return;
    }
    navigate('/select-topics');
  };

  const handleSpectate = () => {
    navigate('/room', { state: { role: 'spectator' } });
  };

  const handleLogin = () => {
    window.location.href = getLoginUrl();
  };

  return (
    <div className="min-h-screen flex flex-col p-4 max-w-lg mx-auto">
      {/* Header */}
      <header className="text-center py-8">
        <div className="flex items-center justify-center gap-2">
          <h1 className="text-3xl font-bold">🎙️ Debate Battle</h1>
          {isAdmin && (
            <button
              onClick={() => navigate('/admin')}
              className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded hover:bg-yellow-500/30 transition-colors"
            >
              🔧 관리자
            </button>
          )}
        </div>
        <p className="text-slate-400 mt-2">실시간 음성 토론 배틀</p>
      </header>

      {/* Guest Banner */}
      {isGuest && (
        <div className="mb-4 p-4 bg-blue-500/20 border border-blue-500/50 rounded-xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-300 font-semibold">👋 게스트로 입장 중</p>
              <p className="text-sm text-blue-300/70 mt-1">로그인하면 토론에 직접 참여할 수 있어요!</p>
            </div>
            <button
              onClick={handleLogin}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold rounded-lg transition-colors"
            >
              로그인
            </button>
          </div>
        </div>
      )}

      {/* User Card */}
      <div className="card mb-6">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-primary-500/20 border-2 border-primary-500 flex items-center justify-center overflow-hidden">
            {user?.avatarUrl ? (
              <img 
                src={user.avatarUrl} 
                alt="프로필" 
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-2xl">{isGuest ? '👤' : '😎'}</span>
            )}
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold">{user?.nickname || '게스트'}</h2>
            {isGuest ? (
              <div className="flex items-center gap-2 mt-1">
                <span className="px-2 py-0.5 bg-slate-600 text-slate-300 rounded text-sm">
                  관전 모드
                </span>
              </div>
            ) : statsLoading ? (
              <div className="flex items-center gap-2 mt-1">
                <span className="px-2 py-0.5 bg-slate-700 rounded text-sm animate-pulse w-12 h-5"></span>
              </div>
            ) : (
              <div className="flex items-center gap-2 mt-1">
                <span className="px-2 py-0.5 bg-primary-500/20 text-primary-300 rounded text-sm">
                  {userStats.tierEmoji} Lv.{userStats.level}
                </span>
                <span className="text-sm text-slate-400">{userStats.title}</span>
              </div>
            )}
          </div>
        </div>

        {/* Quick Stats - 로그인 사용자만 */}
        {!isGuest && (
          <>
            <div className="grid grid-cols-3 gap-3 mt-4">
              <div className="text-center p-2 bg-slate-700/50 rounded-lg">
                <p className="text-lg font-bold">{totalDebates}</p>
                <p className="text-xs text-slate-400">토론</p>
              </div>
              <div className="text-center p-2 bg-slate-700/50 rounded-lg">
                <p className="text-lg font-bold">{userStats.juryCount}</p>
                <p className="text-xs text-slate-400">배심</p>
              </div>
              <div className="text-center p-2 bg-slate-700/50 rounded-lg">
                <p className="text-lg font-bold">{winRateDisplay}%</p>
                <p className="text-xs text-slate-400">승률</p>
              </div>
            </div>

            {/* 전적 상세 */}
            <div className="flex justify-center gap-4 mt-3 text-sm">
              <span className="text-green-400">{userStats.wins}승</span>
              <span className="text-red-400">{userStats.losses}패</span>
              <span className="text-yellow-400">{userStats.draws}무</span>
            </div>

            {/* 배심원 우선권 표시 */}
            {userStats.juryPriority > 0 && (
              <div className="mt-3 p-2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-center">
                <span className="text-xs text-yellow-300">
                  ⭐ 배심원 우선권 {userStats.juryPriority}점 - 토론자 선발 확률 증가!
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Main CTA */}
      <div className="flex-1 flex flex-col items-center justify-center">
        {/* 현재 방 상태 표시 */}
        {roomStatus && (
          <div className="mb-4 text-center">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
              roomStatus.hasActiveGame 
                ? 'bg-yellow-500/20 text-yellow-300' 
                : 'bg-green-500/20 text-green-300'
            }`}>
              <span className={`w-2 h-2 rounded-full ${
                roomStatus.hasActiveGame ? 'bg-yellow-400 animate-pulse' : 'bg-green-400'
              }`} />
              {roomStatus.hasActiveGame ? '토론 진행 중' : '대기 중'}
            </div>
          </div>
        )}
        
        {/* 게스트: 관전만 가능 */}
        {isGuest ? (
          <>
            {roomStatus?.canSpectate ? (
              <>
                <div className="mb-4 p-4 bg-yellow-500/20 border border-yellow-500 rounded-xl text-center">
                  <p className="text-yellow-300 font-semibold">🎙️ 토론 진행 중!</p>
                  <p className="text-sm text-yellow-300/70 mt-1">관전 모드로 토론을 시청하세요</p>
                </div>
                <button
                  onClick={handleSpectate}
                  className="w-full max-w-xs py-5 rounded-2xl bg-gradient-to-r from-yellow-500 to-orange-500 
                             hover:from-yellow-600 hover:to-orange-600 
                             text-white text-lg font-bold shadow-lg shadow-yellow-500/30
                             transition-all duration-300 hover:scale-105 active:scale-95"
                >
                  👁️ 관전하기
                </button>
              </>
            ) : (
              <div className="text-center">
                <div className="mb-4 p-4 bg-slate-700/50 rounded-xl">
                  <p className="text-slate-300">현재 진행 중인 토론이 없습니다</p>
                  <p className="text-sm text-slate-400 mt-1">토론이 시작되면 관전할 수 있어요</p>
                </div>
                <button
                  onClick={handleLogin}
                  className="w-full max-w-xs py-5 rounded-2xl bg-gradient-to-r from-primary-500 to-primary-600 
                             hover:from-primary-600 hover:to-primary-700 
                             text-white text-lg font-bold shadow-lg shadow-primary-500/30
                             transition-all duration-300 hover:scale-105 active:scale-95"
                >
                  🔐 로그인하고 토론 참여하기
                </button>
              </div>
            )}
          </>
        ) : (
          /* 로그인 사용자: 토론 참여 가능 */
          <>
            {roomStatus?.canSpectate ? (
              <>
                <div className="mb-4 p-4 bg-yellow-500/20 border border-yellow-500 rounded-xl text-center">
                  <p className="text-yellow-300 font-semibold">🎙️ 토론 진행 중!</p>
                  <p className="text-sm text-yellow-300/70 mt-1">현재 토론을 관전하거나 다음 게임을 준비하세요</p>
                </div>
                <div className="flex flex-col gap-3 w-full max-w-xs">
                  <button
                    onClick={handleSpectate}
                    className="w-full py-5 rounded-2xl bg-gradient-to-r from-yellow-500 to-orange-500 
                               hover:from-yellow-600 hover:to-orange-600 
                               text-white text-lg font-bold shadow-lg shadow-yellow-500/30
                               transition-all duration-300 hover:scale-105 active:scale-95"
                  >
                    👁️ 관전하기
                  </button>
                  <button
                    onClick={handleStartDebate}
                    className="w-full py-4 rounded-xl bg-slate-700 hover:bg-slate-600
                               text-white font-semibold transition-all"
                  >
                    다음 게임 대기열 참가
                  </button>
                </div>
              </>
            ) : (
              <>
                <button
                  onClick={handleStartDebate}
                  className="w-full max-w-xs py-6 rounded-2xl bg-gradient-to-r from-primary-500 to-primary-600 
                             hover:from-primary-600 hover:to-primary-700 
                             text-white text-xl font-bold shadow-lg shadow-primary-500/30
                             transition-all duration-300 hover:scale-105 active:scale-95"
                >
                  🎤 토론 시작하기
                </button>
                <p className="text-slate-400 text-sm mt-4 text-center">
                  주제를 선택하면 자동으로<br />토론자 또는 배심원으로 참여합니다
                </p>
              </>
            )}
          </>
        )}
      </div>

      {/* How it works */}
      <div className="mt-8 space-y-3">
        <h3 className="text-sm font-semibold text-slate-400 text-center">참여 방법</h3>
        <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg">
          <span className="text-2xl">1️⃣</span>
          <p className="text-sm">주제별로 찬성/반대 의견을 선택해요</p>
        </div>
        <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg">
          <span className="text-2xl">2️⃣</span>
          <p className="text-sm">시스템이 상대와 역할을 자동 배정해요</p>
        </div>
        <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg">
          <span className="text-2xl">3️⃣</span>
          <p className="text-sm">토론하고 배심원 투표로 승패 결정!</p>
        </div>
      </div>

      {/* 하단 광고 배너 */}
      <div className="mt-8 pb-4">
        <AdBanner 
          adSlot={AD_SLOTS.LOBBY_BANNER} 
          adFormat="horizontal"
        />
      </div>
    </div>
  );
}
